import React from 'react';
import { useTranslation } from '../context/LanguageContext';
import './Modal.css';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  isSuccess?: boolean;
  successMessage?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  type = 'warning',
  isLoading = false,
  isSuccess = false,
  successMessage
}) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  const getConfirmButtonClass = () => {
    switch (type) {
      case 'danger':
        return 'btn-danger';
      case 'warning':
        return 'btn-warning';
      case 'info':
        return 'btn-primary';
      default:
        return 'btn-warning';
    }
  };

  const getIconByType = () => {
    if (isLoading) return '⏳';
    
    switch (type) {
      case 'danger':
        return '⚠️';
      case 'warning':
        return '❓';
      case 'info':
        return 'ℹ️';
      default:
        return '❓';
    }
  };

  const getDisplayTitle = () => {
    if (isSuccess) return t('success');
    if (isLoading) return t('loading') + '...';
    return title;
  };

  const getDisplayMessage = () => {
    if (isSuccess) return successMessage || t('success');
    if (isLoading) return t('loading') + '...';
    return message;
  };

  return (
    <div className="modal-overlay" onClick={isLoading || isSuccess ? undefined : onCancel}>
      <div className={`modal-content confirmation-dialog ${isSuccess ? 'success' : ''}`} onClick={(e) => e.stopPropagation()}>
        {isSuccess ? (
          <div className="confirmation-body success">
            <div className="success-checkmark">
              <div className="check-icon">
                <span className="icon-line line-tip"></span>
                <span className="icon-line line-long"></span>
                <div className="icon-circle"></div>
                <div className="icon-fix"></div>
              </div>
            </div>
            <h3 style={{ textAlign: 'center', margin: '0 0 10px 0', color: '#166534', fontSize: '18px', fontWeight: '600' }}>
              {getDisplayTitle()}
            </h3>
            <p style={{ textAlign: 'center', margin: 0 }}>{getDisplayMessage()}</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h3>
                <span className={`confirmation-icon ${isLoading ? 'loading' : ''}`}>{getIconByType()}</span>
                {getDisplayTitle()}
              </h3>
            </div>
            
            <div className="confirmation-body">
              <p>{getDisplayMessage()}</p>
            </div>
          </>
        )}
        
        {!isLoading && !isSuccess && (
          <div className="modal-actions">
            {cancelText && cancelText !== '' && (
              <button
                type="button"
                onClick={onCancel}
                className="btn-secondary"
              >
                {cancelText || t('cancelText')}
              </button>
            )}
            <button
              type="button"
              onClick={onConfirm}
              className={getConfirmButtonClass()}
            >
              {confirmText || t('confirmText')}
            </button>
          </div>
        )}
        
        {isSuccess && (
          <div className="modal-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn-primary"
            >
              {t('close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmationDialog; 